/**
 * TEAM-KPI (founder-approved 2026-10-21; spec 13 §Team KPIs and per-metric bands). Not lifted:
 * CompanyOS's own pure rules, beside the KPI Beast engine and built on it.
 *
 * - Per-metric warning bands: a metric may carry its own thresholds on target attainment
 *   (green ≥ `green`%, amber ≥ `amber`%, red below), overriding the company's fixed bands. The
 *   company default stays the fallback: green from the KPI `great` threshold (100) and amber from
 *   its `low` threshold (70), so a metric without bands colours exactly as before.
 * - Team roll-ups: a team KPI's value is the team's own (entered, imported, or its members'
 *   events), or its member seats' values for the period added up or averaged. Missing is never
 *   zero: a seat without a value is left out, and the roll-up's completeness says how many
 *   seats reported.
 */
import { DEFAULTS } from "./engine/policy";

/** Thresholds on attainment, in percent (100 = on target). */
export type MetricBands = { green: number; amber: number };

/** A metric line's health: the band its attainment falls in, or null with no attainment. */
export type MetricHealth = "green" | "amber" | "red";

/** The company's fixed bands as metric bands: green from `great`, amber from `low` (100 / 70). */
export const DEFAULT_METRIC_BANDS: MetricBands = {
  green: DEFAULTS.kpi_thresholds.great,
  amber: DEFAULTS.kpi_thresholds.low,
};

/** The highest threshold a band may name (the engine caps attainment at 150%). */
export const MAX_BAND = DEFAULTS.attainment_cap * 100;

/** Why `bands` cannot be stored (null: they can). Amber may equal green (no amber zone). */
export function bandsProblem(bands: MetricBands): string | null {
  const { green, amber } = bands;
  if (!Number.isFinite(green) || !Number.isFinite(amber)) return "thresholds are numbers";
  if (amber < 0 || green > MAX_BAND) return `thresholds are between 0 and ${MAX_BAND}% of target`;
  if (amber > green) return "amber starts at or below green";
  return null;
}

/** A metric's own bands, else the company's. */
export function effectiveBands(bands: MetricBands | null | undefined): MetricBands {
  return bands ?? DEFAULT_METRIC_BANDS;
}

/**
 * The health of an attainment (a fraction: 1 = on target, direction-aware, from `attainment`)
 * under `bands` (the company's when absent). No attainment is no health: grey, never red.
 */
export function metricHealth(
  attainment: number | null,
  bands?: MetricBands | null,
): MetricHealth | null {
  if (attainment === null || !Number.isFinite(attainment)) return null;
  const b = effectiveBands(bands);
  const pct = attainment * 100;
  // A float like 0.7 * 100 = 70.00000000000001 still reads 70: compare at 1e-9.
  if (pct >= b.green - 1e-9) return "green";
  if (pct >= b.amber - 1e-9) return "amber";
  return "red";
}

/** How a team KPI gets its value: its own observations, or its member seats' added or averaged. */
export type TeamRollup = "none" | "sum" | "average";

/** One member seat's rolled-up value of a metric for a period (domain `rollup`'s fields). */
export type MemberValue = {
  actual: number | null;
  expected: number;
  present: number;
  completeness: number;
};

/**
 * Combine member seats' values for one period (`sum` or `average`). Seats with no value are
 * left out of both; no seat with a value is no value (null), never 0. `expected` is the period's
 * (the same metric and period for every seat, so the largest of theirs); completeness is the
 * share of members that reported. No members at all: nothing is expected.
 */
export function combineMembers(
  mode: Exclude<TeamRollup, "none">,
  members: readonly MemberValue[],
): MemberValue {
  const expected = members.reduce((m, v) => Math.max(m, v.expected), 0);
  const due = members.filter((v) => v.expected > 0);
  if (due.length === 0) return { actual: null, expected: 0, present: 0, completeness: 1 };
  const valued = due.filter((v) => v.actual !== null);
  if (valued.length === 0) return { actual: null, expected, present: 0, completeness: 0 };
  const total = valued.reduce((s, v) => s + (v.actual as number), 0);
  return {
    actual: mode === "sum" ? total : total / valued.length,
    expected,
    present: valued.length === due.length ? expected : 0,
    completeness: valued.length / due.length,
  };
}

/** Labels for the roll-up choice (the KPI dialog and the team scorecard). */
export const TEAM_ROLLUP_LABELS: Record<TeamRollup, string> = {
  none: "Team's own value",
  sum: "Sum of members' seats",
  average: "Average of members' seats",
};
